import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";
import { Divider } from "antd";

ChartJS.register(
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
);

function UserThinkingChart({
  dataPoints = [],
  updateVideoTrack,
  minThinkingTime,
  maxThinkingTime,
  avgThinkingTime,
  jdAvgThinkingTime,
}) {
  if (!dataPoints || dataPoints.length === 0)
    return (
      <div className="text-center text-gray-500 text-base pt-4">
        No thinking time data points found.
      </div>
    );

  const values = dataPoints?.map((point) => ({
    x: point.elapsedTime,
    y: point.userThinkingTime,
  }));

  const maxX = dataPoints?.length
    ? Math.max(...dataPoints?.map((p) => p.elapsedTime))
    : 0;

  const yValues = dataPoints?.map((p) => p.userThinkingTime);

  const minY = yValues?.length ? Math.min(...yValues) : 0;
  const maxYValue = yValues?.length ? Math.max(...yValues) : 10;
  const avgY = yValues?.length
    ? yValues.reduce((a, b) => a + b, 0) / yValues?.length
    : 0;

  const maxY = Math.ceil(maxYValue / 5) * 5;

  const convertSecondsToTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const createHorizontalLine = (value, label, color, line) => ({
    label,
    data: [
      { x: 0, y: value },
      { x: maxX + 10, y: value },
    ],
    borderColor: color,
    borderWidth: 2,
    borderDash: line ? [] : [6, 6],
    pointRadius: 0,
    fill: false,
    tension: 0,
  });
  const data = {
    datasets: [
      {
        label: "Thinking Time",
        data: values,
        borderColor: "#206DC5",
        backgroundColor: "rgba(32,109,197,0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0.3,

        pointRadius: (context) => {
          const value = context.parsed?.y;
          if (value === minThinkingTime || value === maxThinkingTime) {
            return 6;
          }
          return 4;
        },

        pointBackgroundColor: (context) => {
          const value = context.parsed.y;
          if (value === minThinkingTime) return "#ef4444";
          if (value === maxThinkingTime) return "#22c55e";
          return "rgb(32,109,197)";
        },

        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },

      {
        label: "Minimum",
        borderColor: "#ef4444",
        backgroundColor: "#ef4444",
        pointRadius: 6,
        showLine: false,
      },

      {
        label: "Maximum",
        borderColor: "#22c55e",
        backgroundColor: "#22c55e",
        pointRadius: 6,
        showLine: false,
      },
      createHorizontalLine(avgThinkingTime, "Average", "#c2410c", "line"), // orange
      // createHorizontalLine(minThinkingTime, "Minimum", "#ef4444"), // red
      // createHorizontalLine(maxThinkingTime, "Maximum", "#22c55e"), // green
      createHorizontalLine(jdAvgThinkingTime, "JD Average", "#FDB022"), // green
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: maxX + 10,
        title: {
          display: true,
          text: "Elapsed Time (mm:ss)",
          font: { weight: "bold" },
        },
        ticks: {
          callback: (value) => convertSecondsToTime(value),
        },
      },
      y: {
        min: -1,
        max: maxY,
        title: {
          display: true,
          text: "Time (seconds)",
          font: { weight: "bold" },
        },
        ticks: {
          stepSize: 5,
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "User Thinking Analysis",
        font: {
          size: 18,
          weight: "bold",
        },
        padding: {
          bottom: 7,
        },
      },

      subtitle: {
        display: true,
        text: "(Duration the user spends thinking before answering each question)",
        font: {
          size: 13.6,
        },
        color: "#6b7280",
        padding: {
          bottom: 12,
        },
      },

      legend: {
        display: true,
        position: "top",
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const datasetIndex = legendItem.datasetIndex;
          const label = legendItem.text;

          // Prevent click for Maximum (and Minimum if you want)
          if (label === "Maximum" || label === "Minimum") {
            return;
          }

          // Default toggle behavior for others
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.hidden =
            meta.hidden === null
              ? !chart.data.datasets[datasetIndex].hidden
              : null;

          chart.update();
        },
      },
      tooltip: {
        callbacks: {
          title: (context) =>
            `Elapsed: ${convertSecondsToTime(context[0].parsed.x)}`,
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toFixed(2)} sec`,
        },
      },
    },
    interaction: {
      mode: "nearest",
      intersect: true,
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && updateVideoTrack) {
        const clickedX = elements[0].element.$context.parsed.x;
        updateVideoTrack(clickedX);

        window.scrollTo({
          top: 200,
          behavior: "smooth",
        });
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
  };

  return (
    <div>
      <div className="h-[320px]">
        <Line data={data} options={options} />
      </div>
      <div className="mt-5 max-w-[88rem] mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="mb-[.35rem]">
          <span className="font-semibold">How to read:</span> This graph shows
          the time taken by the user to think before answering each question
          during the interview. A steeper slope indicates a longer thinking time
          for a question.
        </p>

        <p className="text-blue-800">
          <span className="font-semibold">Note:</span> The JD-level average is
          also displayed to help compare the user's average thinking time with
          the overall average for the same role.
        </p>
      </div>
      <Divider className="border-gray-200 mt-8 mb-[-.5rem]" />
    </div>
  );
}

export default UserThinkingChart;
